import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { PreviewReleaseDiffResponse } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import type { Release } from '@sunstone/api-client/gen/releases/v1/releases_pb';
import { ReleaseSyncStatus } from '@sunstone/api-client/hooks';
import { Alert, AlertAction, AlertDescription, AlertTitle, Button } from '@sunstone/ui';
import { AlertCircle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { relativeTime } from '@/lib/time';

export interface ReleaseSyncStatusAlertProps {
  data: PreviewReleaseDiffResponse;
  liveTargetRelease?: Release;
  /** Called when the user clicks "Sync now". */
  onSyncRelease?: () => void;
  /** True while a just-triggered sync is still in progress (covers both the
   * mutation's own in-flight window and the polling wait afterward). */
  isSyncingRelease: boolean;
  syncReleaseError?: Error | null;
}

/**
 * Persistent sync-status banner shown across every response status (READY,
 * FAILED, SYNC_NEEDED) - sync_status/last_synced_at/is_stale/target_release
 * are always populated on PreviewReleaseDiffResponse regardless of status,
 * specifically so this doesn't need a second call. Always paired with a
 * Sync button so the operator can re-trigger a sync from any state, not
 * only SYNC_NEEDED. Purely informational/optional - never blocks a diff
 * from rendering or an action from being taken (see ReleaseDiffDialog and
 * ComponentsDiffPanel, its two consumers, for how the "in progress" state
 * disables Plan/Promote separately rather than through this component).
 */
export function ReleaseSyncStatusAlert({
  data,
  liveTargetRelease,
  onSyncRelease,
  isSyncingRelease,
  syncReleaseError,
}: ReleaseSyncStatusAlertProps) {
  // liveTargetRelease is kept fresh by the caller's live subscription and
  // wins whenever it's loaded - data.targetRelease only refreshes when the
  // diff itself is re-computed, which lags a fresh sync's outcome.
  const targetRelease = liveTargetRelease ?? data.targetRelease;
  const syncStatus = liveTargetRelease?.syncStatus ?? data.syncStatus;
  const isInProgress = isSyncingRelease || syncStatus === ReleaseSyncStatus.IN_PROGRESS;
  const isFailed = syncStatus === ReleaseSyncStatus.FAILED;
  const lastSyncedAt = targetRelease?.lastSyncedAt ?? data.lastSyncedAt;
  const isSucceeded = !isInProgress && !isFailed && !!lastSyncedAt;

  let title: string;
  let message: string;
  if (isInProgress) {
    title = 'Syncing';
    message = 'Sync in progress…';
  } else if (isFailed) {
    title = 'Sync failed';
    message = targetRelease?.syncError || 'The release sync failed.';
  } else if (isSucceeded && lastSyncedAt) {
    title = 'Synced';
    message = `Synced ${relativeTime(timestampDate(lastSyncedAt))}.`;
  } else {
    title = 'Never synced';
    message = 'This release has never been synced.';
  }

  return (
    <Alert variant={isFailed ? 'destructive' : 'default'}>
      {isFailed ? (
        <AlertCircle className="h-4 w-4" />
      ) : isSucceeded ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : (
        <RefreshCw className={isInProgress ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
      )}
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {message}
        {syncReleaseError && (
          <p className="mt-1 text-destructive">
            {syncReleaseError.message ?? 'Failed to start the release sync.'}
          </p>
        )}
      </AlertDescription>
      <AlertAction>
        <Button
          size="sm"
          variant="outline"
          onClick={onSyncRelease}
          disabled={!onSyncRelease || isInProgress}
        >
          {isInProgress ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Sync
        </Button>
      </AlertAction>
    </Alert>
  );
}
